import type { ComputeInput, Id, ObjectPatch, ObjectRecord, Op, Transaction } from '@vp/shared-types';
import type { CanvasDocument } from './document';
import type { History } from './history';

export interface Resolver {
  /** Does this object type compute its state from incoming links? */
  isComputed(type: string): boolean;
  compute(obj: ObjectRecord, inputs: ComputeInput[], signal: AbortSignal, timeoutMs: number): Promise<ObjectPatch | null>;
}

export interface RecomputeOptions {
  /** Per-step timeout; on timeout the dependent is marked stale (§8.3). */
  stepTimeoutMs?: number;
  onError?: (objectId: Id, error: unknown) => void;
  onStale?: (objectId: Id, stale: boolean) => void;
}

const STALE = '$stale';

/**
 * Dependency recompute (§8.3): after a commit, dependents of changed objects are
 * recomputed in topological order. Results are appended to the same history step,
 * so undo reverts both the source change and its dependents. A slow module never
 * blocks the canvas: on timeout the dependent is marked stale and keeps its last
 * state; a late answer updates it when it arrives.
 */
export class Recomputer {
  private unsub: () => void;
  private controllers = new Map<Id, AbortController>();
  private running = Promise.resolve();
  private disposed = false;

  constructor(
    private readonly doc: CanvasDocument,
    private readonly history: History,
    private readonly resolver: Resolver,
    private readonly opts: RecomputeOptions = {},
  ) {
    this.unsub = doc.onCommit((tx, meta) => {
      // undo/redo/replay already contain the dependent updates
      if (meta.history || meta.replay || tx.label === 'recompute') return;
      this.running = this.running.then(() => this.process(tx)).catch(() => undefined);
    });
  }

  dispose(): void {
    this.disposed = true;
    this.unsub();
    for (const c of this.controllers.values()) c.abort();
    this.controllers.clear();
  }

  /** Wait for pending recompute work (tests). */
  idle(): Promise<void> {
    return this.running;
  }

  private changedSources(tx: Transaction): Set<Id> {
    const ids = new Set<Id>();
    for (const op of tx.ops) {
      if (op.table === 'objects') ids.add(op.t === 'update' ? op.id : op.rec.id);
      else {
        // a new/changed link makes its target depend on the (possibly unchanged) source
        const rec = op.t === 'update' ? this.doc.getLink(op.id) : (op.rec as { to?: Id; from?: Id });
        if (rec && 'from' in rec && rec.from) ids.add(rec.from);
      }
    }
    return ids;
  }

  /** Dependents reachable from `sources`, in topological order (Kahn). */
  order(sources: Set<Id>): Id[] {
    const reach = new Set<Id>();
    const stack = [...sources];
    while (stack.length) {
      const id = stack.pop()!;
      for (const l of this.doc.outgoingLinks(id)) {
        if (!reach.has(l.to)) {
          reach.add(l.to);
          stack.push(l.to);
        }
      }
    }
    const indeg = new Map<Id, number>();
    for (const id of reach) indeg.set(id, 0);
    for (const id of reach) for (const l of this.doc.incomingLinks(id)) if (reach.has(l.from)) indeg.set(id, (indeg.get(id) ?? 0) + 1);
    const queue = [...reach].filter((id) => indeg.get(id) === 0);
    const out: Id[] = [];
    while (queue.length) {
      const id = queue.shift()!;
      out.push(id);
      for (const l of this.doc.outgoingLinks(id)) {
        if (!reach.has(l.to)) continue;
        const d = (indeg.get(l.to) ?? 0) - 1;
        indeg.set(l.to, d);
        if (d === 0) queue.push(l.to);
      }
    }
    return out; // cycles are rejected at link creation, so all nodes are ordered
  }

  private async process(tx: Transaction): Promise<void> {
    if (this.disposed) return;
    const sources = this.changedSources(tx);
    if (!sources.size) return;
    const order = this.order(sources);
    const timeout = this.opts.stepTimeoutMs ?? 800;
    for (const id of order) {
      if (this.disposed) return;
      const obj = this.doc.get(id);
      if (!obj || !this.resolver.isComputed(obj.type)) continue;
      const inputs: ComputeInput[] = this.doc.incomingLinks(id).map((link) => ({ link, source: this.doc.get(link.from) ?? null }));
      this.controllers.get(id)?.abort();
      const ac = new AbortController();
      this.controllers.set(id, ac);
      const work = this.resolver.compute(obj, inputs, ac.signal, timeout * 20);
      const timedOut = Symbol('timeout');
      const first = await Promise.race([work.catch((e: unknown) => ({ error: e })), new Promise<typeof timedOut>((r) => setTimeout(() => r(timedOut), timeout))]);
      if (first === timedOut) {
        this.markStale(tx.id, id, true);
        // late answer: apply when it arrives, unless superseded
        void work
          .then((patch) => {
            if (this.controllers.get(id) !== ac || ac.signal.aborted) return;
            this.apply(tx.id, id, patch, true);
          })
          .catch((e: unknown) => {
            if (!ac.signal.aborted) this.opts.onError?.(id, e);
          });
        continue;
      }
      if (first && typeof first === 'object' && 'error' in first) {
        if (!ac.signal.aborted) this.opts.onError?.(id, first.error);
        continue;
      }
      if (this.controllers.get(id) === ac) this.controllers.delete(id);
      this.apply(tx.id, id, first as ObjectPatch | null, false);
    }
  }

  private markStale(txId: Id, id: Id, stale: boolean): void {
    const o = this.doc.get(id);
    if (!o || this.doc.readOnly || !!o.props[STALE] === stale) return;
    const props = { ...o.props };
    if (stale) props[STALE] = true;
    else delete props[STALE];
    this.commitOps(txId, [{ t: 'update', table: 'objects', id, before: { props: o.props }, after: { props } }]);
    this.opts.onStale?.(id, stale);
  }

  private apply(txId: Id, id: Id, patch: ObjectPatch | null, late: boolean): void {
    const o = this.doc.get(id);
    if (!o || this.doc.readOnly) return;
    const after: Record<string, unknown> = {};
    const before: Record<string, unknown> = {};
    if (patch) {
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined) continue;
        if (k === 'props') after[k] = { ...o.props, ...(v as Record<string, unknown>) };
        else if (k === 'style') after[k] = { ...o.style, ...(v as Record<string, unknown>) };
        else after[k] = v;
      }
    }
    if (o.props[STALE] || late) {
      const props = { ...((after['props'] as Record<string, unknown>) ?? o.props) };
      delete props[STALE];
      after['props'] = props;
    }
    if (!Object.keys(after).length) return;
    for (const k of Object.keys(after)) before[k] = (o as Record<string, unknown>)[k];
    this.commitOps(txId, [{ t: 'update', table: 'objects', id, before, after }]);
    if (o.props[STALE]) this.opts.onStale?.(id, false);
  }

  /** Apply ops outside the undo stack and attach them to the originating history step. */
  private commitOps(txId: Id, ops: Op[]): void {
    try {
      const applied = this.doc.applyOps(ops, { source: { kind: 'system' }, label: 'recompute' }, { history: true });
      if (applied) this.history.amend(txId, applied.ops);
    } catch (e) {
      this.opts.onError?.(ops[0] && ops[0].t === 'update' ? ops[0].id : '', e);
    }
  }
}
